import React, { useState, useContext, useEffect } from "react";
import { UrlServicesContext } from "components/UrlServicesContext";

import { makeStyles } from "@material-ui/core/styles";
import {
  Search,
  Layers,
  Save,
  Clear,
  CheckCircle,
  Cancel as CancelIcon,
} from "@material-ui/icons";

import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";

import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import Snackbar from "@material-ui/core/Snackbar";
import Divider from "@material-ui/core/Divider";
import Paper from "@material-ui/core/Paper";
import Chip from "@material-ui/core/Chip";
import MuiAlert from "@material-ui/lab/Alert";

import { cardBodyStyle } from "assets/jss/material-dashboard-react/components/cardBodyStyle";

const useStyles = makeStyles((theme) => ({
  ...cardBodyStyle,
  searchBox: {
    padding: 20,
    background: "#fafafa",
    borderRadius: 8,
    marginBottom: 20,
  },
  resultBox: {
    padding: 24,
    border: "1px solid #e0e0e0",
    borderRadius: 8,
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 0",
    borderBottom: "1px solid #f0f0f0",
  },
  infoLabel: {
    color: "#666",
    fontSize: 13,
    fontWeight: 500,
  },
  infoValue: {
    fontSize: 14,
    fontWeight: 600,
    color: "#222",
  },
  fixerBox: {
    background: "#fff8e1",
    padding: 16,
    borderRadius: 8,
    border: "1px solid #ffe082",
    marginTop: 16,
  },
  fixerLabel: {
    fontSize: 12,
    color: "#795548",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  saveButton: {
    background: "#2e7d32",
    color: "#fff",
    "&:hover": { background: "#1b5e20" },
  },
  emptyState: {
    textAlign: "center",
    padding: 40,
    color: "#999",
  },
}));

const Alert = (props) => <MuiAlert elevation={6} variant="filled" {...props} />;

export default function EditFixerJobAdmin() {
  const classes = useStyles();
  const { urlServices, keyAplication } = useContext(UrlServicesContext);

  const keyAuthorization = localStorage.getItem("Session");

  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [jobData, setJobData] = useState(null);
  const [fixerValue, setFixerValue] = useState("");
  const [searched, setSearched] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const showSnackbar = (severity, message) => {
    setSnackbar({ open: true, severity, message });
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      showSnackbar("error", "Debe ingresar un ID o nombre");
      return;
    }

    setLoading(true);
    setJobData(null);
    setSearched(false);

    try {
      const response = await fetch(
        `${urlServices}jobs-tpm/search/find?q=${encodeURIComponent(searchTerm.trim())}`,
        {
          method: "GET",
          headers: {
            application: keyAplication,
            Authorization: "Bearer " + keyAuthorization,
          },
        }
      );

      if (response.status === 404) {
        setJobData(null);
        setSearched(true);
        showSnackbar("error", "No se encontró ningún cargo con ese criterio");
        return;
      }

      if (!response.ok) {
        throw new Error("Error al consultar");
      }

      const data = await response.json();
      setJobData(data);
      setFixerValue(String(data.fixer ?? ""));
      setSearched(true);
    } catch (e) {
      console.error(e);
      showSnackbar("error", "Error al realizar la búsqueda");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (fixerValue === "" || isNaN(parseFloat(fixerValue))) {
      showSnackbar("error", "El fixer debe ser un número válido");
      return;
    }

    const newFixer = parseFloat(fixerValue);
    if (newFixer < 0) {
      showSnackbar("error", "El fixer no puede ser negativo");
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${urlServices}jobs-tpm/${jobData.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          application: keyAplication,
          Authorization: "Bearer " + keyAuthorization,
        },
        body: JSON.stringify({ fixer: newFixer }),
      });

      if (!response.ok) {
        throw new Error("Error al guardar");
      }

      const updated = await response.json();
      setJobData({ ...jobData, fixer: updated.fixer ?? newFixer });
      showSnackbar(
        "success",
        `Fixer actualizado correctamente para "${jobData.name}"`
      );
    } catch (e) {
      console.error(e);
      showSnackbar("error", "Error al actualizar el fixer");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setSearchTerm("");
    setJobData(null);
    setFixerValue("");
    setSearched(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const formatDate = (date) => {
    if (!date) return "—";
    try {
      return new Date(date).toLocaleString("es-EC");
    } catch {
      return "—";
    }
  };

  const renderInfoRow = (label, value) => (
    <div className={classes.infoRow}>
      <span className={classes.infoLabel}>{label}</span>
      <span className={classes.infoValue}>{value ?? "—"}</span>
    </div>
  );

  const hasFixerChanged =
    jobData && parseFloat(fixerValue) !== parseFloat(jobData.fixer);

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color="info">
            <div className={classes.cardTitleWhite}>
              <Layers className={classes.iconWhite} /> Buscador de Cargo
            </div>
          </CardHeader>
          <CardBody>
            {/* Buscador */}
            <Paper className={classes.searchBox} elevation={0}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Buscar por ID del cargo"
                    placeholder="Ej: 125"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={handleKeyPress}
                   // helperText="Si ingresas solo números, busca por ID exacto."
                    autoFocus
                  />
                </Grid>
                <Grid item xs={6} sm={3} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    startIcon={
                      loading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <Search />
                      )
                    }
                    onClick={handleSearch}
                    disabled={loading || !searchTerm.trim()}
                  >
                    Buscar
                  </Button>
                </Grid>
                <Grid item xs={6} sm={3} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Clear />}
                    onClick={handleClear}
                    disabled={loading}                    
                  >
                    Limpiar
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {/* Resultado */}
            {!searched && !loading && (
              <div className={classes.emptyState}>
                <Search style={{ fontSize: 60, color: "#ddd" }} />
                <div style={{ marginTop: 12 }}>
                  Ingrese un ID o nombre para comenzar la búsqueda
                </div>
              </div>
            )}

            {searched && !jobData && !loading && (
              <div className={classes.emptyState}>
                <CancelIcon style={{ fontSize: 60, color: "#f44336" }} />
                <div style={{ marginTop: 12 }}>
                  No se encontró ningún cargo con "{searchTerm}"
                </div>
              </div>
            )}

            {jobData && (
              <Paper className={classes.resultBox} elevation={1}>
                <Grid container spacing={3}>
                  {/* Cabecera del resultado */}
                  <Grid item xs={12}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 12, color: "#999" }}>
                          ID #{jobData.id}
                        </div>
                        <div
                          style={{
                            fontSize: 20,
                            fontWeight: 700,
                            marginTop: 4,
                          }}
                        >
                          {jobData.name}
                        </div>
                      </div>
                      <Chip
                        label={jobData.status === 1 ? "ACTIVO" : "INACTIVO"}
                        style={{
                          background:
                            jobData.status === 1 ? "#2e7d32" : "#c62828",
                          color: "#fff",
                          fontWeight: 700,
                        }}
                      />
                    </div>
                  </Grid>

                  {/* Info izquierda */}
                  <Grid item xs={12} md={6}>
                    {renderInfoRow("Total Score", jobData.totalScore)}
                    {renderInfoRow("Equivalent", jobData.equivalent)}
                    {renderInfoRow(
                      "Manejo de contraseñas",
                      jobData.managePasswords ? "Sí" : "No"
                    )}
                    {renderInfoRow(
                      "Tiempo de activación",
                      jobData.activationTime ? "Sí" : "No"
                    )}
                  </Grid>

                  {/* Info derecha */}
                  <Grid item xs={12} md={6}>
                    {renderInfoRow(
                      "Tomar foto",
                      jobData.takePhoto ? "Sí" : "No"
                    )}
                    {renderInfoRow(
                      "Mostrar reporte",
                      jobData.showApplicantreport ? "Sí" : "No"
                    )}
                    {renderInfoRow(
                      "Creado",
                      formatDate(jobData.createdAt)
                    )}
                    {renderInfoRow(
                      "Actualizado",
                      formatDate(jobData.updatedAt)
                    )}
                  </Grid>

                  {/* Edición del Fixer */}
                  <Grid item xs={12}>
                    <Divider />
                    <div className={classes.fixerBox}>
                      <div className={classes.fixerLabel}>
                        Modificar Fixer
                      </div>
                      <Grid
                        container
                        spacing={2}
                        alignItems="center"
                        style={{ marginTop: 4 }}
                      >
                        <Grid item xs={12} sm={6} md={5}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            type="number"
                            label="Fixer"
                            value={fixerValue}
                            onChange={(e) => setFixerValue(e.target.value)}
                            inputProps={{
                              step: "0.01",
                              min: "0",
                            }}
                            helperText={`Valor actual en BD: ${jobData.fixer}`}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Button
                            fullWidth
                            variant="contained"
                            className={classes.saveButton}
                            startIcon={
                              saving ? (
                                <CircularProgress
                                  size={16}
                                  color="inherit"
                                />
                              ) : (
                                <Save />
                              )
                            }
                            onClick={handleSave}
                            disabled={
                              saving || !hasFixerChanged || fixerValue === ""
                            }
                          >
                            {saving ? "Guardando..." : "Guardar cambio"}
                          </Button>
                        </Grid>
                        {hasFixerChanged && (
                          <Grid item xs={12} md={3}>
                            <Chip
                              icon={<CheckCircle />}
                              label="Cambio pendiente"
                              style={{
                                background: "#fff3cd",
                                color: "#856404",
                                fontWeight: 600,
                              }}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </div>
                  </Grid>
                </Grid>
              </Paper>
            )}
          </CardBody>
        </Card>
      </GridItem>

      {/* Snackbar de notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </GridContainer>
  );
}